import { Icon } from '@vrplatform/icons';
import { toTitleCase } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  Skeleton,
  Stack,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { MentionNodeAttrs } from '@tiptap/extension-mention';
import type { SuggestionProps } from '@tiptap/suggestion';
import {
  type ElementRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { InfiniteDataProps } from '../../select/SelectDropdown';
import type { SelectItem } from '../../select/_types';

const isDebugEnabled = () => false;

const debugLog = (...args: unknown[]) => {
  if (!isDebugEnabled()) return;
  // biome-ignore lint/suspicious/noConsole: debug logging gated by flag
  console.debug('[FormulaSuggestions]', ...args);
};

type FormulaSuggestionProps = SuggestionProps<SelectItem, MentionNodeAttrs> & {
  queryData: InfiniteDataProps;
};

export const FormulaSuggestions = forwardRef(
  (props: FormulaSuggestionProps, ref) => {
    const queryData = props.queryData;
    const { colorScheme } = useMantineColorScheme();

    useEffect(() => {
      debugLog('render', {
        query: props.query,
        items: props.items?.length ?? 0,
        isFetching: queryData.isFetching,
      });
    }, [props.items, props.query, queryData.isFetching]);

    return (
      <Box
        data-testid="formula-suggestions"
        sx={(theme) => ({
          boxShadow: theme.shadows.md,
          width: 400,
          borderRadius: theme.radius.md,
          border: `1px solid ${
            colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[3]
          }`,
          backgroundColor:
            colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        })}
      >
        <Options
          {...queryData}
          items={props.items}
          containerRef={ref as any}
          command={props.command}
        />
      </Box>
    );
  }
);

const Options = ({
  data,
  hasNextPage,
  error,
  fetchNextPage,
  isFetching,
  isFetchingNextPage,
  containerRef,
  command,
  items,
}: {
  containerRef: ElementRef<'div'>;
  command: (v: MentionNodeAttrs) => void;
  items: SelectItem[];
} & InfiniteDataProps) => {
  const rows = useMemo(() => {
    const queryRows = data?.pages.flatMap((page) => page.list) ?? [];

    if (queryRows.length || isFetching) {
      return queryRows;
    }

    return items ?? [];
  }, [data?.pages, isFetching, items]);

  useEffect(() => {
    debugLog('rows', {
      rows: rows.length,
      isFetching,
      hasNextPage,
      isFetchingNextPage,
    });
  }, [rows.length, isFetching, hasNextPage, isFetchingNextPage]);

  const parentRef = useRef<ElementRef<'div'>>(null);

  const virtualizer = useVirtualizer({
    count: hasNextPage ? rows.length + 1 : rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 38,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= rows.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    rows.length,
    isFetchingNextPage,
    virtualItems,
  ]);

  const defaultHeight = 300;

  const listHeight = virtualizer.getTotalSize();

  const showSkeletons = isFetching && !isFetchingNextPage;

  const getParentHeight = () => {
    if (showSkeletons) return 80;

    return listHeight > defaultHeight ? defaultHeight : listHeight || 50;
  };

  const parentHeight = getParentHeight();

  const { focusedIndex } = useKeyboardNavigation({
    containerRef,
    items: rows,
    handleSelect: (index) => {
      const item = rows[index];
      if (item) {
        command({ id: item.value, label: item.label });
      }
    },
    scrollToIndex: virtualizer.scrollToIndex,
  });

  return (
    <Box
      ref={parentRef}
      className="List"
      data-testid="formula-suggestions-list"
      style={{
        height: parentHeight,
        width: '100%',
        overflowY: 'auto',
        contain: 'strict',
      }}
      sx={(theme) => ({
        borderRadius: theme.radius.md,
      })}
    >
      <div
        style={{
          height: '100%',
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
          }}
        >
          {showSkeletons ? (
            <Stack gap={'xs'} p={5} data-testid="formula-suggestions-loading">
              <Skeleton height={15} w="100%" />
              <Skeleton height={15} w="100%" />
              <Skeleton height={15} w="100%" />
            </Stack>
          ) : error ? (
            <Group
              sx={{ height: '100%' }}
              p={5}
              pt="sm"
              wrap="nowrap"
              justify="center"
            >
              <Center>
                <Icon
                  icon="AlertTriangleIcon"
                  color={(theme) => theme.colors.red[6]}
                  size={18}
                />
              </Center>
              <Text component="span" size="sm" c="red">
                {error.message}
              </Text>
            </Group>
          ) : !items.length ? (
            <Center sx={{ height: '100%' }} p={5} pt="sm">
              <Text component="span" size="sm" c="gray">
                No suggestions found
              </Text>
            </Center>
          ) : (
            virtualItems.map((virtualRow) => {
              const index = virtualRow.index;

              const isLoaderRow = index > rows.length - 1;
              const item = rows[index];

              const isFocused = index === focusedIndex;

              const onClick = () => {
                command({ id: item.value, label: item.label });
              };

              return (
                <div
                  key={virtualRow.key}
                  data-index={index}
                  ref={virtualizer.measureElement}
                >
                  {isLoaderRow ? (
                    hasNextPage ? (
                      <Box
                        px={'xs'}
                        pb={5}
                        data-testid="formula-suggestions-loading-more"
                      >
                        <Skeleton height={15} w="100%" />
                      </Box>
                    ) : null
                  ) : (
                    <ListItem
                      isFocused={isFocused}
                      item={item}
                      onClick={onClick}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Box>
  );
};

const ListItem = ({
  isFocused,
  item,
  onClick,
}: {
  item: SelectItem;
  isFocused: boolean;
  onClick: () => void;
}) => {
  const { colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  const description = item.description;

  const badge = useMemo(() => {
    const type = item.group?.toLowerCase();

    if (type === 'accounts' || type === 'account') {
      return {
        name: 'Account',
        color: colors.blue[6],
      };
    }
    if (type === 'variables') {
      return {
        name: 'Variable',
        color: colors.grape[6],
      };
    }
    if (type === 'formulas') {
      return {
        name: 'Formula',
        color: colors.orange[6],
      };
    }
    if (type === 'fields') {
      return {
        name: 'Field',
        color: colors.teal[6],
      };
    }
    if (type === 'collections') {
      return {
        name: 'Collection',
        color: colors.cyan[6],
      };
    }
    if (type === 'column') {
      return {
        name: 'Column',
        color: colors.cyan[6],
      };
    }

    if (type === 'functions') {
      return {
        name: 'Function',
        color: colors.green[6],
      };
    }

    return {
      name: toTitleCase(type),
      color: colors.blue[6],
    };
  }, [
    item.group,
    colors.blue[6],
    colors.cyan[6],
    colors.grape[6],
    colors.green[6],
    colors.orange[6],
    colors.teal[6],
  ]);

  return (
    <Box
      component="button"
      onMouseDown={(e) => {
        // mousedown instead of onClick to prevent onBlur on tiptap input
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      sx={(theme) => {
        return {
          display: 'flex',
          flexDirection: 'row',
          gap: 5,
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingInline: theme.spacing.xs,
          paddingBlock: 5,
          border: 'none',
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          backgroundColor: isFocused ? theme.colors.neutral[1] : 'transparent',
          ':hover': {
            backgroundColor:
              colorScheme === 'dark'
                ? theme.colors.neutral[7]
                : theme.colors.neutral[1],
          },
        };
      }}
    >
      <Text
        component="span"
        size="sm"
        sx={{
          wordBreak: 'break-word',
          flex: 1,
          lineHeight: description ? 1.2 : undefined,
        }}
      >
        {item.label}
        {description && (
          <>
            <br />
            <Text
              size="xs"
              c={colorScheme === 'dark' ? colors.neutral[4] : 'gray'}
              component="span"
            >
              {description}
            </Text>
          </>
        )}
      </Text>
      {item.group && (
        <Text
          component="span"
          size="sm"
          m={0}
          ml="auto"
          c={badge?.color}
          ta="right"
          sx={{
            flexShrink: 0,
          }}
        >
          {badge?.name}
        </Text>
      )}
    </Box>
  );
};

function useKeyboardNavigation({
  containerRef,
  items,
  handleSelect,
  scrollToIndex,
}: {
  items: SelectItem[];
  containerRef: any;
  handleSelect: (index: number) => void;
  scrollToIndex: (index: number) => void;
}) {
  const [focusedIndex, setFocusedIndex] = useState(0);

  const upHandler = () => {
    if (!items.length) return;
    const newIndex = (focusedIndex + items.length - 1) % items.length;
    setFocusedIndex(newIndex);
    scrollToIndex(newIndex);
  };

  const downHandler = () => {
    if (!items.length) return;
    const newIndex = (focusedIndex + 1) % items.length;
    setFocusedIndex(newIndex);
    scrollToIndex(newIndex);
  };

  useEffect(() => setFocusedIndex(0), [items]);

  useImperativeHandle(containerRef as any, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        if (items.length) {
          handleSelect(focusedIndex);
        }
        return true;
      }

      return false;
    },
  }));

  return {
    focusedIndex,
  };
}
